/**
 * Mantine UI Widgets for React JSON Schema Form
 *
 * Maps React JSON Schema Form widget types to Mantine components.
 * Preserves all Mantine component capabilities.
 * Domain-agnostic: Works for any form field type.
 */

import React from 'react';
import {
  TextInput,
  Textarea,
  NumberInput,
  Select,
  MultiSelect,
  Checkbox,
  Switch,
  PasswordInput,
  FileInput,
  ColorInput,
  Slider,
  Rating,
  SegmentedControl,
  Autocomplete,
} from '@mantine/core';
import { DateInput, DateTimePicker, TimeInput } from '@mantine/dates';
import { WidgetProps } from '@rjsf/utils';

/**
 * Mantine TextInput widget
 */
export function MantineTextInput(props: WidgetProps) {
  const {
    id,
    value,
    required,
    disabled,
    readonly,
    label,
    placeholder,
    onChange,
    onBlur,
    onFocus,
    schema,
    uiSchema,
    rawErrors,
  } = props;

  return (
    <TextInput
      id={id}
      label={label}
      placeholder={placeholder}
      value={value || ''}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.currentTarget.value)}
      onBlur={() => onBlur(id, value)}
      onFocus={() => onFocus(id, value)}
      required={required}
      disabled={disabled || readonly}
      error={rawErrors && rawErrors.length > 0 ? rawErrors[0] : undefined}
      description={schema.description}
    />
  );
}

/**
 * Mantine Textarea widget
 */
export function MantineTextarea(props: WidgetProps) {
  const {
    id,
    value,
    required,
    disabled,
    readonly,
    label,
    placeholder,
    onChange,
    rawErrors,
    schema,
    uiSchema,
  } = props;

  return (
    <Textarea
      id={id}
      label={label}
      placeholder={placeholder}
      value={value || ''}
      onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => onChange(event.currentTarget.value)}
      required={required}
      disabled={disabled || readonly}
      error={rawErrors && rawErrors.length > 0 ? rawErrors[0] : undefined}
      description={schema.description}
      minRows={3}
    />
  );
}

/**
 * Mantine NumberInput widget
 */
export function MantineNumberInput(props: WidgetProps) {
  const {
    id,
    value,
    required,
    disabled,
    readonly,
    label,
    placeholder,
    onChange,
    rawErrors,
    schema,
    uiSchema,
  } = props;

  return (
    <NumberInput
      id={id}
      label={label}
      placeholder={placeholder}
      value={value !== undefined ? Number(value) : undefined}
      onChange={(val: string | number) => onChange(val)}
      required={required}
      disabled={disabled || readonly}
      error={rawErrors && rawErrors.length > 0 ? rawErrors[0] : undefined}
      description={schema.description}
      min={schema.minimum}
      max={schema.maximum}
    />
  );
}

/**
 * Mantine Select widget
 */
export function MantineSelect(props: WidgetProps) {
  const {
    id,
    value,
    required,
    disabled,
    readonly,
    label,
    placeholder,
    onChange,
    rawErrors,
    schema,
    options,
    uiSchema,
  } = props;

  const selectOptions =
    options.enumOptions?.map((opt) => ({
      value: String(opt.value),
      label: opt.label,
    })) || [];

  return (
    <Select
      id={id}
      label={label}
      placeholder={placeholder}
      value={value ? String(value) : null}
      onChange={(val: string | null) => onChange(val)}
      data={selectOptions}
      required={required}
      disabled={disabled || readonly}
      error={rawErrors && rawErrors.length > 0 ? rawErrors[0] : undefined}
      description={schema.description}
      searchable
      clearable={!required}
    />
  );
}

/**
 * Mantine MultiSelect widget
 */
export function MantineMultiSelect(props: WidgetProps) {
  const {
    id,
    value,
    required,
    disabled,
    readonly,
    label,
    placeholder,
    onChange,
    rawErrors,
    schema,
    options,
    uiSchema,
  } = props;

  const selectOptions =
    options.enumOptions?.map((opt) => ({
      value: String(opt.value),
      label: opt.label,
    })) || [];

  return (
    <MultiSelect
      id={id}
      label={label}
      placeholder={placeholder}
      value={Array.isArray(value) ? value.map(String) : []}
      onChange={(val: string[]) => onChange(val)}
      data={selectOptions}
      required={required}
      disabled={disabled || readonly}
      error={rawErrors && rawErrors.length > 0 ? rawErrors[0] : undefined}
      description={schema.description}
      searchable
      clearable
    />
  );
}

/**
 * Mantine Checkbox widget
 */
export function MantineCheckbox(props: WidgetProps) {
  const { id, value, required, disabled, readonly, label, onChange, rawErrors, schema, uiSchema } =
    props;

  return (
    <Checkbox
      id={id}
      label={label}
      checked={Boolean(value)}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.currentTarget.checked)}
      required={required}
      disabled={disabled || readonly}
      error={rawErrors && rawErrors.length > 0 ? rawErrors[0] : undefined}
      description={schema.description}
    />
  );
}

/**
 * Mantine Switch widget
 */
export function MantineSwitch(props: WidgetProps) {
  const { id, value, required, disabled, readonly, label, onChange, rawErrors, schema, uiSchema } =
    props;

  return (
    <Switch
      id={id}
      label={label}
      checked={Boolean(value)}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.currentTarget.checked)}
      required={required}
      disabled={disabled || readonly}
      error={rawErrors && rawErrors.length > 0 ? rawErrors[0] : undefined}
      description={schema.description}
    />
  );
}

/**
 * Mantine DateInput widget
 */
export function MantineDateInput(props: WidgetProps) {
  const {
    id,
    value,
    required,
    disabled,
    readonly,
    label,
    placeholder,
    onChange,
    rawErrors,
    schema,
    uiSchema,
  } = props;

  return (
    <DateInput
      id={id}
      label={label}
      placeholder={placeholder}
      value={value ? new Date(value) : null}
      onChange={(date: string | null) => {
        if (!date) {
          onChange(null);
          return;
        }
        onChange(date);
      }}
      required={required}
      disabled={disabled || readonly}
      error={rawErrors && rawErrors.length > 0 ? rawErrors[0] : undefined}
      description={schema.description}
      clearable={!required}
    />
  );
}

/**
 * Mantine DateTimePicker widget
 */
export function MantineDateTimePicker(props: WidgetProps) {
  const {
    id,
    value,
    required,
    disabled,
    readonly,
    label,
    placeholder,
    onChange,
    rawErrors,
    schema,
    uiSchema,
  } = props;

  return (
    <DateTimePicker
      id={id}
      label={label}
      placeholder={placeholder}
      value={value ? new Date(value) : null}
      onChange={(date: string | null) => {
        if (!date) {
          onChange(null);
          return;
        }
        onChange(date);
      }}
      required={required}
      disabled={disabled || readonly}
      error={rawErrors && rawErrors.length > 0 ? rawErrors[0] : undefined}
      description={schema.description}
      clearable={!required}
    />
  );
}

/**
 * Mantine PasswordInput widget
 */
export function MantinePasswordInput(props: WidgetProps) {
  const {
    id,
    value,
    required,
    disabled,
    readonly,
    label,
    placeholder,
    onChange,
    rawErrors,
    schema,
    uiSchema,
  } = props;

  return (
    <PasswordInput
      id={id}
      label={label}
      placeholder={placeholder}
      value={value || ''}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.currentTarget.value)}
      required={required}
      disabled={disabled || readonly}
      error={rawErrors && rawErrors.length > 0 ? rawErrors[0] : undefined}
      description={schema.description}
    />
  );
}

/**
 * Export all Mantine widgets
 */
export const MANTINE_WIDGETS = {
  TextInput: MantineTextInput,
  Textarea: MantineTextarea,
  NumberInput: MantineNumberInput,
  Select: MantineSelect,
  MultiSelect: MantineMultiSelect,
  Checkbox: MantineCheckbox,
  Switch: MantineSwitch,
  DateInput: MantineDateInput,
  DateTimePicker: MantineDateTimePicker,
  PasswordInput: MantinePasswordInput,
};
